import React, { useEffect, useState, useCallback } from 'react';
import LifecycleBadge from './LifecycleBadge';
import VersionDiffPanel from './VersionDiffPanel';
import { usePlaybookVersions } from '../hooks/usePlaybookVersions';
import type { PlaybookEntity } from '../types/playbook.types';

interface VersionHistoryTabProps {
  playbook: PlaybookEntity;
}

const VersionHistoryTab: React.FC<VersionHistoryTabProps> = ({ playbook }) => {
  const { versions, loading, error, diff, diffLoading, loadVersions, loadDiff, clearDiff } = usePlaybookVersions(playbook.slug);
  const [compareSource, setCompareSource] = useState<string | null>(null);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  const handleCompare = useCallback((versionId: string) => {
    // Compare this version against the current (latest) version
    const currentVersion = versions.find((v) => v.lifecycle === 'active') || versions[0];
    if (currentVersion && currentVersion.id !== versionId) {
      setCompareSource(versionId);
      loadDiff(versionId, currentVersion.id);
    }
  }, [versions, loadDiff]);

  if (loading) {
    return (
      <div className="flex items-center py-4">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-lex-accent border-t-transparent" />
        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">Loading versions...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Diff panel */}
      {diff && (
        <VersionDiffPanel diff={diff} onClose={clearDiff} />
      )}
      {diffLoading && (
        <div className="flex items-center py-2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-lex-accent border-t-transparent" />
          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">Loading diff...</span>
        </div>
      )}

      {/* Version list */}
      {versions.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-4">No version history available.</p>
      ) : (
        <div className="space-y-1">
          {versions.map((v) => (
            <div
              key={v.id}
              className={`flex items-center gap-3 py-2 px-3 rounded-md ${
                compareSource === v.id
                  ? 'bg-lex-accent-soft border border-lex-accent'
                  : 'bg-gray-50 dark:bg-gray-800/50'
              }`}
            >
              <span className="text-sm font-bold text-gray-900 dark:text-white w-10">v{v.version}</span>
              <LifecycleBadge lifecycle={v.lifecycle} />
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {v.statusCount} statuses, {v.transitionCount} transitions
              </span>
              {v.instanceCount > 0 && (
                <span className="text-[10px] font-medium text-lex-accent">
                  {v.instanceCount} instance{v.instanceCount !== 1 ? 's' : ''}
                </span>
              )}
              {v.published_at && (
                <span className="text-[10px] text-gray-400 dark:text-gray-500">
                  Published {new Date(v.published_at).toLocaleDateString()}
                </span>
              )}
              <div className="ml-auto flex items-center gap-1">
                {versions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleCompare(v.id)}
                    className="rounded border border-gray-300 px-2 py-0.5 text-[10px] font-medium text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
                  >
                    Compare
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VersionHistoryTab;
